import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { 
  FileText, Download, CheckCircle, Clock, AlertCircle,
  Loader2, ClipboardList, Shield, Camera, Heart
} from 'lucide-react';

const formTypes = [
  { value: 'liability_waiver', label: 'Liability Waiver', icon: Shield, description: 'Release of liability for participation' },
  { value: 'photo_release', label: 'Photo/Video Release', icon: Camera, description: 'Permission for photo/video use' },
  { value: 'medical_form', label: 'Medical Information', icon: Heart, description: 'Emergency contacts and medical info' },
  { value: 'code_of_conduct', label: 'Code of Conduct', icon: ClipboardList, description: 'Student behavior agreement' },
  { value: 'completion_certificate', label: 'Completion Certificate', icon: FileText, description: 'Course completion certificate' },
];

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800',
  signed: 'bg-green-100 text-green-800',
  declined: 'bg-red-100 text-red-800',
};

export default function AdminPDFFormsPage({ embedded = false }) {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [learners, setLearners] = useState([]);
  const [courses, setCourses] = useState([]);
  const [sections, setSections] = useState([]);
  const [formLogs, setFormLogs] = useState([]);
  const [selectedLearner, setSelectedLearner] = useState('');
  const [selectedSection, setSelectedSection] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [learnersRes, coursesRes, sectionsRes, logsRes] = await Promise.all([
        api.get('/learners/'),
        api.get('/courses/'),
        api.get('/sections/'),
        api.get('/forms/logs')
      ]);
      // Handle both paginated and non-paginated responses
      setLearners(learnersRes.data?.results || learnersRes.data || []);
      setCourses(coursesRes.data?.results || coursesRes.data || []);
      setSections(sectionsRes.data?.results || sectionsRes.data || []);
      setFormLogs(logsRes.data?.results || logsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      // Demo data
      setLearners([
        { id: 'l1', first_name: 'Emma', last_name: 'Smith', parent_id: 'p1' },
        { id: 'l2', first_name: 'Lucas', last_name: 'Garcia', parent_id: 'p2' }
      ]);
      setCourses([
        { id: 'c1', title: 'Python Fundamentals' },
        { id: 'c2', title: 'Scratch Game Design' }
      ]);
      setFormLogs([]);
    } finally {
      setLoading(false);
    }
  };

  const generateForm = async (formType) => {
    if (!selectedLearner) {
      toast.error('Please select a learner');
      return;
    }
    
    setGenerating(true);
    try {
      const response = await api.post('/forms/generate', {
        form_type: formType,
        learner_id: selectedLearner,
        section_id: selectedSection && selectedSection !== 'none' ? selectedSection : undefined
      }, { responseType: 'blob' });
      
      // Download the PDF
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${formType}_${selectedLearner.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Form generated and downloaded!');
      fetchData(); // Refresh logs
    } catch (error) {
      toast.error('Failed to generate form');
    } finally {
      setGenerating(false);
    }
  };

  const generateAttendanceReport = async () => {
    if (!selectedSection || selectedSection === 'none') {
      toast.error('Please select a section');
      return;
    }
    
    setGenerating(true);
    try {
      const response = await api.get(`/forms/attendance-report/${selectedSection}`, { responseType: 'blob' });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `attendance_report_${selectedSection.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Attendance report generated!');
    } catch (error) {
      toast.error('Failed to generate attendance report');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-pdf-forms-page">
      <div>
        <h1 className="font-heading text-2xl md:text-3xl font-bold mb-2">PDF Forms & Waivers</h1>
        <p className="text-muted-foreground">Generate waivers, medical forms, certificates, and reports</p>
      </div>

      {/* Selection */}
      <Card className="card-dashboard">
        <CardHeader>
          <CardTitle>Generate Forms</CardTitle>
          <CardDescription>Select a learner and optionally a section to generate forms</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Learner</label>
              <Select value={selectedLearner} onValueChange={setSelectedLearner}>
                <SelectTrigger data-testid="select-learner">
                  <SelectValue placeholder="Select a learner" />
                </SelectTrigger>
                <SelectContent>
                  {learners.map(learner => (
                    <SelectItem key={learner.id} value={learner.id}>
                      {learner.first_name} {learner.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium">Section (for certificates)</label>
              <Select value={selectedSection} onValueChange={setSelectedSection}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a section (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- None --</SelectItem>
                  {sections.map(section => {
                    const course = courses.find(c => c.id === section.course_id);
                    return (
                      <SelectItem key={section.id} value={section.id}>
                        {course?.title || 'Unknown'} - {section.term}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Form Types */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {formTypes.map(formType => {
          const Icon = formType.icon;
          return (
            <Card key={formType.value} className="card-dashboard">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Icon className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium">{formType.label}</h3>
                    <p className="text-sm text-muted-foreground">{formType.description}</p>
                  </div>
                </div>
                <Button 
                  variant="outline" 
                  className="w-full mt-4"
                  onClick={() => generateForm(formType.value)}
                  disabled={generating || !selectedLearner}
                  data-testid={`generate-${formType.value}`}
                >
                  {generating ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Download className="h-4 w-4 mr-2" />
                  )}
                  Generate PDF
                </Button>
              </CardContent>
            </Card>
          );
        })}
        
        {/* Attendance Report Card */}
        <Card className="card-dashboard border-dashed">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-secondary/20 flex items-center justify-center">
                <ClipboardList className="h-5 w-5 text-secondary" />
              </div>
              <div className="flex-1">
                <h3 className="font-medium">Attendance Report</h3>
                <p className="text-sm text-muted-foreground">Generate attendance report for a section</p>
              </div>
            </div>
            <Button 
              variant="outline" 
              className="w-full mt-4"
              onClick={generateAttendanceReport}
              disabled={generating || !selectedSection || selectedSection === 'none'}
            >
              {generating ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Generate Report
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Form Logs */}
      <Card className="card-dashboard">
        <CardHeader>
          <CardTitle>Form History</CardTitle>
          <CardDescription>Previously generated forms and their signature status</CardDescription>
        </CardHeader>
        <CardContent>
          {formLogs.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No forms generated yet</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Form Type</TableHead>
                  <TableHead>Learner</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Generated</TableHead>
                  <TableHead>Signed</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {formLogs.map(log => {
                  const learner = learners.find(l => l.id === log.learner_id);
                  return (
                    <TableRow key={log.id}>
                      <TableCell className="capitalize">
                        {log.form_type?.replace(/_/g, ' ')}
                      </TableCell>
                      <TableCell>
                        {learner ? `${learner.first_name} ${learner.last_name}` : 'Unknown'}
                      </TableCell>
                      <TableCell>
                        <Badge className={statusColors[log.status]}>
                          {log.status === 'signed' && <CheckCircle className="h-3 w-3 mr-1" />}
                          {log.status === 'pending' && <Clock className="h-3 w-3 mr-1" />}
                          {log.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {new Date(log.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        {log.signed_at ? new Date(log.signed_at).toLocaleDateString() : '-'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
